import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Globe, Volume2, Loader2, Check, ArrowRight } from 'lucide-react';
import {
  getTier1Languages,
  getLanguageByCode,
  getStoredLanguage,
  setStoredLanguage,
  LANGUAGE_PICKER_TEXT,
  ELEVENLABS_CONFIG
} from '../lib/languages';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { useTranslation } from '../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Dev mode feature flag for showing English helpers
const SHOW_ENGLISH_HELPERS = process.env.REACT_APP_SHOW_ENGLISH_HELPERS === 'true';

export default function LanguagePickerPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setPreferredLanguage } = useAuth();
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [playingAudio, setPlayingAudio] = useState(null);
  const [audioRef, setAudioRef] = useState(null);
  const [saving, setSaving] = useState(false);

  const languages = getTier1Languages();

  // Get the redirect path from location state, or determine based on auth
  const redirectTo = location.state?.redirectTo || (user ? '/analytics' : '/signup');
  const isInviteFlow = location.state?.isInvite || false;
  const inviteToken = location.state?.inviteToken || null;

  // Check for existing language preference
  useEffect(() => {
    const storedLang = getStoredLanguage();
    if (storedLang) {
      setSelectedLanguage(storedLang);
    }
  }, []);

  // Get UI text based on selected language or default to English
  const getText = (key) => {
    const lang = selectedLanguage || 'en';
    // Handle zh-CN mapping
    const langKey = lang === 'zh-CN' ? 'zh-CN' : lang;
    const texts = LANGUAGE_PICKER_TEXT[langKey] || LANGUAGE_PICKER_TEXT['en'];
    return texts[key] || LANGUAGE_PICKER_TEXT['en'][key];
  };

  // Get English text for dev mode helpers
  const getEnglishText = (key) => {
    return LANGUAGE_PICKER_TEXT['en'][key];
  };

  const handleLanguageSelect = (langCode) => {
    setSelectedLanguage(langCode);
    // Stop any playing audio
    if (audioRef) {
      audioRef.pause();
      setAudioRef(null);
      setPlayingAudio(null);
    }
  };

  const handlePlaySample = async (langCode, e) => {
    e.stopPropagation();

    const lang = getLanguageByCode(langCode);
    if (!lang || !lang.samplePhrase) return;

    // Stop current audio if playing
    if (audioRef) {
      audioRef.pause();
      setAudioRef(null);
    }

    if (playingAudio === langCode) {
      setPlayingAudio(null);
      return;
    }

    setPlayingAudio(langCode);

    try {
      const response = await api.post('/ai/tts', {
        text: lang.samplePhrase,
        lang: langCode
      });

      if (response?.audio_url) {
        const audio = new Audio(`${API_URL}${response.audio_url}`);
        setAudioRef(audio);

        audio.onended = () => {
          setPlayingAudio(null);
          setAudioRef(null);
        };

        audio.onerror = () => {
          setPlayingAudio(null);
          setAudioRef(null);
          toast.error('Failed to play audio sample');
        };

        audio.play();
      }
    } catch (error) {
      console.error('TTS error:', error);
      setPlayingAudio(null);
      toast.error('Failed to generate audio sample');
    }
  };

  const handleContinue = async () => {
    if (!selectedLanguage) {
      toast.error('Please select a language to continue');
      return;
    }

    setSaving(true);

    try {
      // Store the language preference
      setStoredLanguage(selectedLanguage);

      // If authenticated, also save to server via context
      if (user) {
        await setPreferredLanguage(selectedLanguage);
      }

      // Navigate based on context
      if (isInviteFlow && inviteToken) {
        navigate(`/invite/${inviteToken}`, {
          state: { language: selectedLanguage }
        });
      } else if (redirectTo) {
        navigate(redirectTo, {
          state: { language: selectedLanguage }
        });
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Error saving language preference:', error);
      toast.error('Failed to save language preference');
    } finally {
      setSaving(false);
    }
  };

  // Check if we should show English helper for a non-English UI
  const showDevHelper = SHOW_ENGLISH_HELPERS && selectedLanguage && selectedLanguage !== 'en';

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-3xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <img src="/assets/pocketfiler-header-logo.png" alt="PocketFiler" className="h-12" />
          </div>
          <h1 className="text-3xl font-display font-bold mb-3">
            {getText('title')}
            {showDevHelper && (
              <span className="block text-sm font-normal text-muted-foreground mt-1">
                ({getEnglishText('title')})
              </span>
            )}
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            {getText('subtitle')}
            {showDevHelper && (
              <span className="block text-xs text-muted-foreground/70 mt-1 italic">
                ({getEnglishText('subtitle')})
              </span>
            )}
          </p>
        </div>

        {/* Language Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-8">
          {languages.map((lang) => (
            <Card
              key={lang.code}
              className={`cursor-pointer transition-all hover:shadow-md ${selectedLanguage === lang.code
                ? 'ring-2 ring-primary bg-primary/5'
                : 'hover:bg-muted/50'
                }`}
              onClick={() => handleLanguageSelect(lang.code)}
              data-testid={`language-option-${lang.code}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-lg truncate" dir={lang.rtl ? 'rtl' : 'ltr'}>
                      {lang.nativeName}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {lang.name}
                    </p>
                  </div>
                  {selectedLanguage === lang.code && (
                    <Check className="h-5 w-5 text-primary flex-shrink-0 ml-2" />
                  )}
                </div>

                {/* Play Sample Button */}
                {lang.features.voice && lang.samplePhrase && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full mt-2 text-xs"
                    onClick={(e) => handlePlaySample(lang.code, e)}
                    disabled={playingAudio && playingAudio !== lang.code}
                    aria-label={`Play ${lang.name} sample`}
                  >
                    {playingAudio === lang.code ? (
                      <>
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        {getText('playing')}
                      </>
                    ) : (
                      <>
                        <Volume2 className="h-3 w-3 mr-1" />
                        {getText('playSample')}
                      </>
                    )}
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Continue Button */}
        <div className="flex justify-center">
          <Button
            size="lg"
            onClick={handleContinue}
            disabled={!selectedLanguage || saving}
            className="min-w-48"
            data-testid="language-continue-button"
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                {getText('continue')}
                {showDevHelper && ` (${getEnglishText('continue')})`}
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </div>

        {/* Cross-language explanation */}
        {selectedLanguage && (
          <div className="mt-8 text-center">
            <Card className="bg-muted/30 border-dashed">
              <CardContent className="py-4">
                <p className="text-sm text-muted-foreground">
                  You chose <strong>{getLanguageByCode(selectedLanguage)?.name}</strong>.
                  <br />
                  We will show everything to you in this language.
                  <br />
                  Other people can choose their own language; PocketFiler translates messages, documents, and contracts so everyone can understand.
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Dev mode indicator */}
        {SHOW_ENGLISH_HELPERS && (
          <div className="mt-4 text-center">
            <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
              Dev Mode: English helpers enabled
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
